import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@component/card";
import BasicLayout from "@container/layout/basic";
import { getAssessmentCompanyDemo, triggerDemoNotification } from "@service/assessment";
import { DemoCompanyInfo } from "@type/company";
import { DemoInterviewInsight } from "@type/interview";
import { removeCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { convertMarkdownToHtml } from "@util/string";
import { cn } from "@util/tailwind";
import { FocusIcon, TargetIcon, TrendingUpIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface UserFormData {
    email: string;
    yourName: string;
}

export default function InterviewInsightDemo() {
    const { companyname, conversation_id } = useParams()
    const [companyInfo, setCompanyInfo] = useState<DemoCompanyInfo | undefined>(undefined);
    const [isGenerating, setIsGenerating] = useState<boolean>(true);
    const [data, setData] = useState<DemoInterviewInsight>({
        strength: ["Demonstrated engagement and willingness to participate in the interview process. More detailed answers provide richer insights. This is just a sample of what's possible."],
        focus_area: ["Provide more specific examples and details in your responses. More detailed answers provide richer insights. This is just a sample of what's possible."],
        key_improvement: ["Practice answering common interview questions to build confidence. More detailed answers provide richer insights. This is just a sample of what's possible."]
    });

    useEffect(() => {
        removeCookie("interview_conversation_id");
    }, [])

    useEffect(() => {
        if (companyname) {
            getAssessmentCompanyDemo(companyname).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                }
            })
        }
    }, [companyname])

    useEffect(() => {
        if (conversation_id) {
            setData(localStorage.getItem("demo_interview_insights" + conversation_id) ? JSON.parse(localStorage.getItem("demo_interview_insights" + conversation_id) || "{}") : {
                strength: ["Demonstrated engagement and willingness to participate in the interview process. More detailed answers provide richer insights. This is just a sample of what's possible."],
                focus_area: ["Provide more specific examples and details in your responses. More detailed answers provide richer insights. This is just a sample of what's possible."],
                key_improvement: ["Practice answering common interview questions to build confidence. More detailed answers provide richer insights. This is just a sample of what's possible."]
            });
            setIsGenerating(false);
        }
    }, [conversation_id])

    const [activeTab, setActiveTab] = useState<
        "strengths" | "needs-improvement" | "improvement-plan"
    >("strengths");

    // Check if we need Indonesian translation
    const isIndonesian = companyname === "kopi-kenangan";

    // Translation labels
    const labels = {
        pageTitle: isIndonesian ? "Hasil Wawancara Anda" : "Your Interview Insights",
        pageDescription: isIndonesian
            ? "Analisis mendalam performa wawancara Anda untuk pengembangan karir."
            : "Gain a clear understanding of your performance with data-driven insights for continuous improvement.",
        strengthsLabel: isIndonesian ? "Kelebihan" : "Strengths",
        focusAreasLabel: isIndonesian ? "Area yang Perlu Diperbaiki" : "Focus Areas",
        improvementPlanLabel: isIndonesian ? "Rencana Pengembangan" : "Improvement Plan",
        generatingInsights: isIndonesian ? "Sedang menganalisis hasil wawancara..." : "Generating your insights...",
        noItems: isIndonesian ? "Belum ada data di kategori ini." : "No items in this category yet.",
    };

    const tabs = [
        {
            id: "strengths",
            label: labels.strengthsLabel,
            count: data.strength.length,
            color: "text-green-600 border-green-500",
            icon: TrendingUpIcon,
        },
        {
            id: "needs-improvement",
            label: labels.focusAreasLabel,
            count: data.focus_area.length,
            color: "text-amber-600 border-amber-500",
            icon: FocusIcon,
        },
        {
            id: "improvement-plan",
            label: labels.improvementPlanLabel,
            count: data.key_improvement.length,
            color: "text-blue-600 border-blue-500",
            icon: TargetIcon,
        },
    ] as const;

    const getCurrentItems = () => {
        switch (activeTab) {
            case "strengths":
                return data.strength;
            case "needs-improvement":
                return data.focus_area;
            case "improvement-plan":
                return data.key_improvement;
            default:
                return [
                    labels.noItems,
                ];
        }
    };

    const getCurrentColor = () => {
        switch (activeTab) {
            case "strengths":
                return "text-green-600";
            case "needs-improvement":
                return "text-amber-600";
            case "improvement-plan":
                return "text-blue-600";
            default:
                return "text-gray-600";
        }
    };

    // Version without layout (for embedding in other components)
    return (
        <BasicLayout
            customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
            disableMinHeight
            hideNavbarMenu
            style={{
                height: "calc(100vh - 57px)", // Adjust height to fit below the header
            }}
            showFooter={false}
        >
            <div className="w-full">
                <div className="flex flex-row items-center justify-center mt-24 sm:mt-10 mb-10">
                    <div className="flex justify-center">
                        <img
                            src={companyInfo?.company_logo_interview || ""}
                            alt={companyInfo?.company_name || ""}
                            className="h-16 w-auto object-contain"
                        />
                    </div>
                </div>
                <Card className="w-full max-w-4xl mx-auto max-h-[600px] overflow-hidden flex flex-col">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-bold">
                            {labels.pageTitle}
                        </CardTitle>
                        <CardDescription>
                            {labels.pageDescription}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto">
                        <div className="w-full">
                            {/* Tab Navigation */}
                            <div className="border-b border-gray-200 mb-6">
                                <nav className="flex space-x-8">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={cn(
                                                "py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center",
                                                activeTab === tab.id
                                                    ? `${tab.color} border-current`
                                                    : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                                            )}
                                        >
                                            <tab.icon className="w-4 h-4 mr-2" />

                                            {tab.label}
                                            {!isGenerating && (
                                                <span
                                                    className={cn(
                                                        "ml-2 py-0.5 px-2 rounded-full text-xs font-medium transition-colors duration-200 w-7 h-7 flex items-center justify-center",
                                                        activeTab === tab.id
                                                            ? (() => {
                                                                switch (tab.id) {
                                                                    case "strengths":
                                                                        return "bg-green-50 text-green-700 border border-green-100";
                                                                    case "needs-improvement":
                                                                        return "bg-amber-50 text-amber-700 border border-amber-100";
                                                                    case "improvement-plan":
                                                                        return "bg-blue-50 text-blue-700 border border-blue-100";
                                                                    default:
                                                                        return "bg-gray-100 text-gray-600 border border-gray-200";
                                                                }
                                                            })()
                                                            : "bg-gray-100 text-gray-600 border border-gray-200"
                                                    )}
                                                    style={{ paddingTop: "4px", paddingBottom: "4px" }}
                                                >
                                                    {tab.count}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            {/* Content */}
                            <div className="space-y-4 min-h-[300px]">
                                {isGenerating ? (
                                    // skeleton loader
                                    <div className="w-full h-full min-h-[400px] flex flex-col items-center justify-center">
                                        <div className="flex space-x-2 mb-4">
                                            {/* Three bouncing blue dots */}
                                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "0ms" }}></div>
                                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "150ms" }}></div>
                                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "300ms" }}></div>
                                        </div>
                                        <p className="text-gray-600 text-sm">
                                            {labels.generatingInsights}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {(getCurrentItems() || []).length > 0 && (
                                            <ul className="space-y-3">
                                                {(getCurrentItems() || []).map((item, index) => (
                                                    <li key={index} className="flex items-start space-x-3">
                                                        <div
                                                            className={cn(
                                                                "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                                                                getCurrentColor().replace("text-", "bg-")
                                                            )}
                                                        />

                                                        <div
                                                            className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                            dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(item) }}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </BasicLayout >
    );
}
